<template>
  <main class="phone" :class="{ 'phone--tut': tutorial }">
    <!-- No resource strip here: "let's just put that on the TV, it's taking
         up too much room on my phone." The greyed buttons say what is short. -->
    <!--
      The top bar: what you have, in the icons the cards spend. Above the
      role tabs and outside the scroll, so it is always the first thing on
      the screen and never scrolls away.
    -->
    <PurseStrip v-if="!isFinished && dayRole" :civ="game.civ" :workers="myWorkers" :role="dayRole" :dawn="phase === PHASES.DAWN" />

    <section class="phone__body">
      <!-- Finished -->
      <FinishPanel v-if="isFinished" :game="game">
        <button class="primary" @click="leave">Back to the menu</button>
      </FinishPanel>

      <!--
        Dawn: the split is a band across the top and your own board is right
        underneath it, greyed. Matt: "I can't even see what my role does...
        so it's almost impossible for me to evaluate what I might need
        workers for." The board is fed the PROPOSED split, so the numbers on
        it move as the band moves.
      -->
      <template v-else-if="phase === PHASES.DAWN">
        <AllocationPicker
          :game="game"
          :readonly="!canSteer"
          :viewing="dayRole"
          :mine="myRoles"
          @view="chosenPreview = $event"
          @allocate="allocate"
          @propose="propose"
          :tapped="iSaidStart"
          :waiting-names="waitingNames"
          :ready-names="readyNames"
          @start="startDay"
        />
        <template v-if="myRoles.length">
          <!-- The split's chips preview a role's board too, but nobody found
               that (Matt, playtest 11, on an iPad: "I can only see one of my
               two roles... I would've expected there to be tabs"). So with
               more than one role, the day's tabs sit here at dawn as well. -->
          <nav v-if="myRoles.length > 1" class="role-tabs role-tabs--dawn">
            <button
              v-for="role in myRoles"
              :key="role"
              class="role-tab"
              :class="{ 'role-tab--on': role === dayRole }"
              :style="{ '--role': ROLE_INFO[role].colour }"
              @click="chosenPreview = role"
            >{{ ROLE_INFO[role].label }}<span class="role-tab__count">{{ allocation[role] || 0 }}</span></button>
          </nav>
          <RoleBoard :game="dawnPreviewGame" :role="dayRole" compact preview @act="() => {}" />
        </template>
      </template>

      <!-- The day: everyone plays at once; each of my roles has its hands -->
      <template v-else-if="phase === PHASES.DAY && !dayRole">
        <h2 class="phone__heading">The day</h2>
        <p class="phone__note phone__note--lookup">You hold no role in this game. Watch the table.</p>
      </template>
      <template v-else-if="phase === PHASES.DAY">
        <!-- The tabs are the first thing on the screen; what you hold comes
             under them, not over them. One role: one chip, not a control. -->
        <nav class="role-tabs" :class="{ 'role-tabs--single': myRoles.length === 1 }">
          <button
            v-for="role in myRoles"
            :key="role"
            class="role-tab"
            :class="{ 'role-tab--on': role === dayRole, 'role-tab--done': game.done[role] }"
            :style="{ '--role': ROLE_INFO[role].colour }"
            :disabled="myRoles.length === 1"
            @click="chosenPreview = role"
          >{{ ROLE_INFO[role].label }}<template v-if="myRoles.length === 1"> · {{ game.done[role] ? 'done ✓' : leftWords(role) }}</template><span v-else class="role-tab__count">{{ game.done[role] ? '✓' : (role === 'culture' ? hallLeft(civFor(game, 'culture')).playable : workersFor(role, hands[role])) }}</span></button>
          <!-- What the role is FOR, always in sight (the learner, 2026-10-12;
               Matt: "make it clear what the roles are each doing"). Beside a
               lone tab it costs no height. -->
          <span v-if="myRoles.length === 1" class="role-job">{{ ROLE_INFO[dayRole].job }}</span>
        </nav>
        <p v-if="myRoles.length > 1" class="role-job">{{ ROLE_INFO[dayRole].job }}</p>
        <!--
          Done keeps the board on the screen, greyed, and can be taken back
          while it is still day. Playtest 6: "the done for the day buttons
          clear away my screen... once you do it, you can't go back."
        -->
        <!-- The others, a tap away (a look, not their board). The "Ask for…"
             button that sat here went after playtest 10 (Matt: "that's done
             with voice" - a table talks, in the room or on a call). -->
        <div v-if="!tutorial" class="others">
          <span v-if="otherRoles.length" class="others__label">Others</span>
          <button v-for="r in otherRoles" :key="r" class="others__role" :style="{ '--role': ROLE_INFO[r].colour }" @click="peekRole = r">
            {{ ROLE_INFO[r].label }}<template v-if="game.done[r]"> ✓</template>
          </button>
        </div>
        <p v-if="game.done[dayRole]" class="phone__note phone__waiting">{{ waitingWords }}</p>
        <!-- Nothing left to spend: say so, rather than leave a board that looks
             broken (playtest 9: "I didn't know there wasn't much to be done"). -->
        <p v-else-if="spentNote" class="phone__note phone__note--spent">{{ spentNote }}</p>
        <!-- A fresh board after the tutorial (the learner, 2026-10-12): the
             practice game's card, selection and camera came back with it,
             and the Frontier's map was left on Units or blank. -->
        <RoleBoard :key="tutorial ? 'practice' : 'real'" :game="game" :role="dayRole" :readonly="game.done[dayRole]" :hold="Boolean(outcome)" compact @act="act" @undo="undo(dayRole)" />
        <!--
          THE undo button, the same on every board (Matt, 2026-09-22: "a
          standardized undo button for all roles"). It names what it would
          take back, and when it cannot, says why: a die was rolled, cards
          were drawn, the pool has been spent since.
        -->
        <!-- Pinned to the foot of the screen (playtest 7: "annoying to have to
             scroll down to my done for the day button and then scroll back up"). -->
        <div class="phone__dock">
        <button v-if="!game.done[dayRole]" class="secondary phone__undo" :class="{ 'phone__undo--off': !undoState.ok }" :disabled="!canSteer || !undoState.ok" @click="undo(dayRole)">
          <span class="phone__done-label">{{ undoState.ok ? `Undo ${undoState.label}` : (undoState.label ? `Undo ${undoState.label}` : 'Undo') }}</span>
          <small v-if="!undoState.ok && undoState.label" class="action__sub">{{ undoState.why }}</small>
        </button>
        <button v-if="!game.done[dayRole]" class="secondary phone__done" data-tut="done" :disabled="!canSteer" @click="done(dayRole)">
          <span class="phone__done-label">{{ nextRole ? `Done → ${ROLE_INFO[nextRole].label}` : 'Done for the day' }}</span>
          <small class="action__sub">{{ unusedNote }}</small>
        </button>
        <!-- Done reads as DONE (the learner: "Not done yet / take the day
             back" read as an undo, or as a bar hiding the Done button). -->
        <button v-if="game.done[dayRole]" class="secondary phone__done phone__done--finished" data-tut="done" :disabled="!canSteer" @click="undone(dayRole)">
          <span class="phone__done-label">✓ Done, waiting on others</span>
          <small class="action__sub">not done after all? tap to take your day back</small>
        </button>
        </div>
      </template>

      <!-- The year's end: December's night is over, the chronicle is on the
           table, and the sitting can stop here. Done opens the new year. -->
      <template v-else-if="isYearEnd">
        <h2 class="phone__heading">Year {{ game.yearEnd?.year || '' }} is over</h2>
        <p class="phone__note phone__note--lookup">The table shows the year's chronicle. The sitting ends here; the next one starts in January.</p>
        <button class="primary" :disabled="!canSteer" @click="continueRound">
          Open year {{ (game.yearEnd?.year || 0) + 1 }}
          <small class="action__sub">Nothing changes until then.</small>
        </button>
      </template>

      <!-- The tally: everything comes back to the middle -->
      <template v-else-if="phase === PHASES.TALLY">
        <h2 class="phone__heading">The day is done</h2>
        <p class="phone__note phone__note--lookup">The table shows the day's totals, and the next card face down.</p>
        <!-- Everybody says flip; it turns when the last one does (playtest 9). -->
        <button class="primary" :disabled="!canSteer || iSaidFlip" @click="voteFlip(false)">
          {{ iSaidFlip ? 'Ready to flip' : 'Flip the card' }}
          <small class="action__sub">{{ waiting.length ? `Waiting on ${waitingNames}` : 'Everyone is ready' }}</small>
        </button>
        <!-- A small text link, not a second big button (playtest 11, Matt
             and Nick: "it should be smaller"). -->
        <button v-if="iSaidFlip && waiting.length" class="vote-force" :disabled="!canSteer" @click="voteFlip(true)">Flip it now, without {{ waitingNames }}</button>
      </template>

      <!-- The event -->
      <!-- The card on every phone as well as the table (Matt, playtest 10:
           "it feels weird that the card is up on the TV and the choices are
           down on the board... let's get that done before the next day").
           It had been table-only since playtest 7. -->
      <template v-else-if="phase === PHASES.EVENT && game.event">
        <h2 class="phone__heading">The world turns</h2>
        <EventCard :card-id="game.event.cardId" :event="game.event" face-up class="phone__event-card" />
        <p v-if="!game.event.resolved && game.players.length > 1" class="phone__note phone__note--lookup">Everyone votes on it.</p>
        <div v-if="!game.event.resolved" class="choices">
          <!-- Everybody votes; a split is settled by one ballot drawn at
               random, so 3-1 is 75/25 (Matt, playtest 9). -->
          <p v-if="game.players.length > 1" class="phone__note vote-note">Everyone votes. A split is decided by chance, weighted by the votes.</p>
          <button
            v-for="c in choices"
            :key="c.id"
            class="primary choice"
            :class="{ 'choice--mine': myVote === c.id }"
            :disabled="!canSteer || !c.ok"
            @click="resolve(c.id)"
          >
            {{ c.label }}
            <small class="action__sub">{{ c.text || (c.effectText ? `${c.effectText}.` : 'Nothing changes now.') }}<template v-if="c.story"> More cards follow.</template><strong v-if="c.consequence" class="choice__consequence"> {{ c.consequence }}</strong></small>
            <small v-if="votersFor(c.id).length" class="choice__votes">✓ {{ votersFor(c.id).join(', ') }}</small>
          </button>
          <p v-if="myVote && waiting.length" class="phone__note">Waiting on {{ waitingNames }}.</p>
          <button v-if="myVote && waiting.length" class="vote-force" :disabled="!canSteer" @click="decideNow">Decide now, with the votes in so far</button>
        </div>
        <button v-else class="primary" :disabled="!canSteer" @click="continueRound">
          End the round
          <small class="action__sub">{{ upkeepPreview }}</small>
        </button>
      </template>

      <div class="phone__tail" aria-hidden="true" />
    </section>

    <RolePeek v-if="peekRole" :game="game" :role="peekRole" @close="peekRole = null" />
    <!-- The night, card by card: any one phone moves everyone on (after playtest 12). -->
    <Teleport to="body"><NightCards :game="game" /></Teleport>
    <VoteSpin v-if="spin" :tally="spin.tally" :picked="spin.picked" :labels="spin.labels" @close="spin = null" />

    <!--
      A role's first day offers the TUTORIAL, not a page of text (playtest 9:
      "I eventually stopped reading it and just started tapping buttons";
      Matt: "I know how to play this game and I find it dense and
      overwhelming"). The same small card for every role, in the middle of
      every screen.
    -->
    <Teleport to="body">
      <div v-if="offerFor && !tutorial" class="howto-backdrop" @click.self="closeOffer">
        <div class="howto tut-offer" role="dialog" aria-modal="true" :aria-label="`Learn the ${ROLE_INFO[offerFor].label}`" :style="{ '--role': ROLE_INFO[offerFor].colour }">
          <div class="howto__body">
            <h3>New to the {{ ROLE_INFO[offerFor].label }}?</h3>
            <p>{{ ROLE_INFO[offerFor].tagline }}.</p>
            <p>Take the tutorial: a practice day on your own board, a step at a time. It takes a few minutes, and nothing in it touches the real game.</p>
          </div>
          <div class="howto__nav">
            <button class="secondary" @click="closeOffer">Not now</button>
            <span class="howto__dots" />
            <button class="primary howto__next" @click="takeTutorial">Start the tutorial</button>
          </div>
        </div>
      </div>
    </Teleport>

    <!--
      THE CONFIRM. Every act on every board comes through `act` below; with
      the setting on, it stops here first and says exactly what it will cost
      and what it will make - worked out by running the real reducer on a
      copy, so it is the truth, not a guess. What a die or the glass would
      show stays hidden: those say what they COULD make. Tick the box and
      this kind of act never asks again on this phone.
    -->
    <!--
      THE OUTCOME CARD (Matt, 2026-09-28, pacing: "the only evidence that
      anything happened is in the log... make sure that everyone understands
      what happens after their actions"). After every act, on the actor's
      own phone: what it cost, what it made, what the dice did. Built from
      the act's log entry, so it is the truth and not a guess. Off in the
      menu ("Show what happened").
    -->
    <Teleport to="body">
      <div v-if="outcome" class="howto-backdrop encounter-backdrop outcome-backdrop" @click.self="closeOutcome">
        <div class="howto encounter outcome" role="dialog" aria-modal="true" :aria-label="outcome.title" :style="{ '--people': outcome.colour }">
          <div class="howto__body">
            <span class="encounter__kind">{{ outcome.kind }}</span>
            <h3 class="encounter__title">{{ outcome.title }}</h3>
            <div v-if="outcome.rolls && outcome.rolls.length" class="outcome__dice" :class="{ 'outcome__dice--many': outcome.rolls.length > 4 }">
              <Die3d v-for="(d, j) in outcome.rolls" :key="j" :value="d" :faces="outcome.sides" :phase="outcome.phase" :duration="outcome.each[j]" />
            </div>
            <div class="confirm__ledger">
              <span v-if="outcome.paid.length" class="confirm__row">
                <span class="confirm__what">You paid</span>
                <span class="confirm__pips"><Pip v-for="p in outcome.paid" :key="`p${p.kind}`" :kind="p.kind" :n="p.n" /></span>
              </span>
              <span class="confirm__row">
                <span class="confirm__what">{{ outcome.gained.length ? 'You got' : 'What happened' }}</span>
                <span v-if="outcome.gained.length" class="confirm__pips"><Pip v-for="p in outcome.gained" :key="`g${p.kind}`" :kind="p.kind" :n="p.n" signed /></span>
                <span v-else class="confirm__gain">{{ outcome.said || 'Nothing you can count.' }}</span>
              </span>
            </div>
            <p v-if="outcome.gained.length && outcome.said" class="encounter__text confirm__blurb">{{ outcome.said }}</p>
          </div>
          <div class="howto__nav">
            <span class="howto__dots" />
            <button class="primary howto__next" @click="closeOutcome">Got it</button>
          </div>
        </div>
      </div>
    </Teleport>
    <!-- The Scholars learned something that changes this board: say what. -->
    <Teleport to="body">
      <div v-if="techNews && !outcome && !confirm" class="howto-backdrop encounter-backdrop outcome-backdrop" @click.self="closeTechNews">
        <div class="howto encounter outcome tech-news" role="dialog" aria-modal="true" :aria-label="`New technology: ${techNews.name}`" :style="{ '--people': techNews.colour }">
          <div class="howto__body">
            <span class="encounter__kind">{{ techNews.kind === 'building' ? 'The Stewards built something for you' : techNews.kind === 'counselor' ? 'Someone could sit on the council' : 'The Scholars learned a technology' }}</span>
            <h3 class="encounter__title">{{ techNews.name }}</h3>
            <p class="encounter__text"><strong v-if="techNews.kind !== 'counselor'">For {{ techNews.forAll ? 'everyone' : `the ${techNews.roles}` }}:</strong> {{ techNews.text }}</p>
            <p v-if="techNews.unlocks" class="encounter__text"><strong>Opens:</strong> {{ techNews.unlocks }}</p>
            <p class="encounter__text confirm__blurb">{{ techNews.kind === 'counselor' ? 'See "who could sit" under the council.' : 'It is in play now; your board shows it.' }}</p>
          </div>
          <div class="howto__nav">
            <span class="howto__dots" />
            <button class="primary howto__next" @click="closeTechNews">Got it</button>
          </div>
        </div>
      </div>
    </Teleport>
    <Teleport to="body">
      <div v-if="confirm" class="howto-backdrop encounter-backdrop" @click.self="closeConfirm">
        <div class="howto encounter confirm" role="dialog" aria-modal="true" :aria-label="confirm.title" :style="{ '--people': confirm.colour }">
          <div class="howto__body">
            <span class="encounter__kind">{{ confirm.kind }}</span>
            <h3 class="encounter__title">{{ confirm.title }}</h3>
            <div class="confirm__ledger">
              <span class="confirm__row">
                <span class="confirm__what">You pay</span>
                <ActionCost :cost="confirm.cost" :as-shifts="confirm.role === 'trade'" :as-actions="confirm.role === 'military'" />
              </span>
              <span class="confirm__row">
                <span class="confirm__what">You get</span>
                <span v-if="confirm.gainPips.length" class="confirm__pips">
                  <Pip v-for="p in confirm.gainPips" :key="p.kind" :kind="p.kind" :n="p.n" signed />
                </span>
                <span class="confirm__gain">{{ confirm.gainText }}</span>
              </span>
            </div>
            <p v-if="confirm.blurb" class="encounter__text confirm__blurb">{{ confirm.blurb }}</p>
            <label class="confirm__skip">
              <input v-model="confirmSkip" type="checkbox" />
              <span>Stop asking me to confirm {{ confirm.short }}</span>
            </label>
          </div>
          <div class="howto__nav">
            <button class="secondary" @click="closeConfirm">Never mind</button>
            <span class="howto__dots" />
            <button class="primary howto__next" @click="confirmGo">Do it</button>
          </div>
        </div>
      </div>
    </Teleport>
  </main>
</template>

<script>
import NightCards from './NightCards.vue';
import { mapState, mapGetters } from 'vuex';
import {
  PHASES, ROLE_INFO,
} from '@/engine/constants';
import { EVENTS } from '@/engine/cards';
import { checkAction } from '@/engine/roleActions';
import {
  foodEatenPerRound, handsOf, allocationOf, undoCheck, dispatch as runReducer, actAction, civFor,
  YEAR_END, logTotal, waitingOn,
} from '@/engine/game';
import { actionsForRole, resolveLabel, resolveCost } from '@/engine/roleActions';
import { COUNTED } from '@/engine/effects';
import { settings, confirmWanted, skipConfirm } from '@/settings';
import ActionCost from './ActionCost.vue';
import Pip from './Pip.vue';
import Die3d from './Die3d.vue';
import { SPENDABLE } from './resourceArt';
import { villagesOf, villageYield } from '@/engine/roleActions';
import {
  ROLES, workersFor, shiftsFor, tokenWord,
} from '@/engine/constants';
import {
  hallOf, freeSeats, hallLeft, hallLeftWords, HALL_CARDS, COUNSELORS, emerging, councilOf,
} from '@/engine/hall';
import { BUILDINGS } from '@/engine/city';
import { canAfford, describeGain } from '@/engine/effects';
import AllocationPicker from './AllocationPicker.vue';
import PurseStrip from './PurseStrip.vue';
import RoleBoard from './RoleBoard.vue';
import FinishPanel from './FinishPanel.vue';
import VoteSpin from '@/components/shared/VoteSpin.vue';
import RolePeek from '@/components/shared/RolePeek.vue';
import EventCard from './EventCard.vue';
import { GUIDE_SEEN_KEY } from '@/guides';
import { help } from '@/help';
import { GOALS, goalOf, TECHS } from '@/engine/cards';

const joinNames = (names) => (names.length <= 2 ? names.join(' and ') : `${names.slice(0, -1).join(', ')} and ${names.at(-1)}`);


export default {
  name: 'PhoneView',
  components: {
    AllocationPicker, PurseStrip, RoleBoard, FinishPanel, ActionCost, Pip, Die3d, VoteSpin, RolePeek, EventCard, NightCards,
  },
  data() {
    return {
      // The act waiting on a "Do it", with what it will cost and make.
      confirm: null,
      confirmSkip: false,
      // The act just fired, waiting for its log entry: { role, from }.
      awaiting: null,
      // What the last act did, for the outcome card.
      outcome: null,
      // A technology the Scholars just learned that changes THIS phone's
      // board (Matt, playtest 10: "a pop-up on their screen that explains
      // the new technology... so they know what's now possible").
      techNews: null,
      techQueue: [],
      settings,
      PHASES,
      ROLE_INFO,
      ROLES,
      // Dev only: ?preview=phone&role=military opens on that role's board.
      chosenPreview: import.meta.env.DEV ? new URLSearchParams(window.location.search).get('role') : null,
      offerFor: null,
      // A split vote that just landed, being shown.
      spin: null,
      peekRole: null,
    };
  },
  computed: {
    // Who the day is still waiting on, once this role is done.
    waitingWords() {
      const left = ROLES.filter((r) => !this.game.done?.[r]).map((r) => ROLE_INFO[r].label);
      if (!left.length) return 'Your day is done ✓ Everyone is done; the day is ending.';
      return `Your day is done ✓ Waiting on ${left.join(', ')}.`;
    },
    ...mapState(['game', 'roomId', 'tutorial']),
    ...mapGetters(['isFinished', 'isMyTurn', 'myRoles', 'myPlayerId', 'playerName']),
    isYearEnd() { return this.game.meta.phase === YEAR_END; },
    waiting() { return waitingOn(this.game); },
    otherRoles() { return ROLES.filter((r) => !this.myRoles.includes(r)); },
    waitingNames() { return joinNames(this.waiting.map((id) => this.firstName(id))); },
    iSaidFlip() { return Boolean(this.game.flipVotes?.[this.myPlayerId]); },
    iSaidStart() { return Boolean(this.game.startVotes?.[this.myPlayerId]); },
    readyNames() { return joinNames(Object.keys(this.game.startVotes || {}).filter((id) => id !== this.myPlayerId).map((id) => this.firstName(id))); },
    techKey() { return `${this.roomId || 'local'}|${(this.game?.civ?.techs || []).join(',')}`; },
    buildKey() { return `${this.roomId || 'local'}|${(this.game?.civ?.city?.buildings || []).map((b) => b.id).join(',')}`; },
    counselorKey() { return `${this.roomId || 'local'}|${this.game?.civ?.hall ? emerging(this.game.civ, this.game.meta?.round || 1).join(',') : ''}`; },
    myVote() { return this.game.event?.votes?.[this.myPlayerId] ?? null; },
    voteResult() { return this.game.event?.vote || null; },
    logCount() { return logTotal(this.game); },
    phase() { return this.game.meta.phase; },
    goal() { return GOALS[goalOf(this.game.civ)] || null; },
    hands() { return handsOf(this.game); },
    allocation() { return allocationOf(this.game); },
    // Nothing left to spend: say so, rather than leave a board that looks
    // broken (playtest 9: "I didn't know there wasn't much to be done").
    // The Hall's workers are its cards (2026-10-14), so for the Hall it is
    // the hand and the seats that run out, not workers.
    spentNote() {
      if (this.dayRole === 'culture') {
        const civ = civFor(this.game, 'culture');
        if (!hallOf(civ)) return '';
        if (!hallOf(civ).hand.length) return 'Nothing left in hand for today. Tap Done for the day when you\'re ready.';
        if (!freeSeats(civ).length) return 'Every counselor has taken a card today. Tap Done for the day when you\'re ready.';
        return '';
      }
      if (this.hands[this.dayRole]) return '';
      return `Every ${this.dayRole === 'trade' ? 'shift' : 'worker'} is used for today. Tap Done for the day when you're ready.`;
    },
    // The Stewards count in shifts; the Hall in cards; everyone else in people.
    unusedNote() {
      if (this.dayRole === 'culture') {
        const hand = hallOf(civFor(this.game, 'culture'))?.hand?.length || 0;
        return hand ? `${hand} ${hand === 1 ? 'card' : 'cards'} unplayed` : 'every card played';
      }
      const n = this.hands[this.dayRole] || 0;
      // Shifts for the Stewards, actions for the Frontier (2026-10-15).
      const word = tokenWord(this.dayRole, 1);
      if (!n) return `every ${word} used`;
      return `${n} ${tokenWord(this.dayRole, n)} unused`;
    },
    // At dawn the strip shows what this role is BEING given; in the day,
    // what it has left.
    myWorkers() {
      if (!this.dayRole) return 0;
      // In the role's own tokens at dawn too (the Stewards' 1 worker is 2 shifts).
      if (this.phase === PHASES.DAWN) return shiftsFor(this.dayRole, this.allocation[this.dayRole] || 0);
      // The Hall's day is counted in cards in hand (its workers became them).
      if (this.dayRole === 'culture') return hallOf(civFor(this.game, 'culture'))?.hand?.length || 0;
      return this.hands[this.dayRole];
    },
    // At dawn the board is shown as it WOULD be, with the workers this role
    // is currently being given. Nothing is tappable (preview), so a day that
    // has not started cannot be played.
    dawnPreviewGame() {
      return {
        ...this.game,
        meta: { ...this.game.meta, phase: PHASES.DAY },
        // In the role's own tokens, as START_DAY hands them out: the
        // Stewards' 3 workers are 6 shifts (the dawn preview said 3).
        hands: Object.fromEntries(Object.entries(this.allocation).map(([r, n]) => [r, shiftsFor(r, n)])),
        done: this.game.done || {},
        taken: this.game.taken || {},
      };
    },
    // Co-op: anyone seated may steer the shared decisions.
    canSteer() { return this.game.players.some((p) => p.id === this.myPlayerId); },
    // Which of my roles the phone is showing during the day: the chosen
    // tab, else the first one still at work, else the first.
    // The role Done will move you on to, if you hold another still at work.
    nextRole() {
      const i = this.myRoles.indexOf(this.dayRole);
      const order = [...this.myRoles.slice(i + 1), ...this.myRoles.slice(0, i)];
      return order.find((r) => r !== this.dayRole && !this.game.done?.[r]) || null;
    },
    dayRole() {
      if (this.myRoles.includes(this.chosenPreview)) return this.chosenPreview;
      return this.myRoles.find((r) => !this.game.done?.[r]) || this.myRoles[0];
    },
    choices() {
      const card = EVENTS[this.game.event?.cardId];
      return (this.game.event?.choices || card?.choices || []).map((c) => ({
        id: c.id,
        label: c.label,
        text: c.text,
        ok: !c.requires || canAfford(this.game.civ, c.requires),
        effectText: describeGain(c.effect || {}),
        // What it does to the neighbours, said before you pick (playtest,
        // 2026-09-25: "Send them home" made the Iron Suns hostile unannounced).
        consequence: c.stance ? `They become ${c.stance}.` : (c.warparty ? 'They send a war party.' : ''),
        // Which card comes next stays sight unseen; THAT one comes is fair to say.
        story: Boolean(c.adds),
      }));
    },
    // What the night will do to the larder, counted the way the night
    // counts it: the villages send up their food FIRST, then the people
    // eat. Playtest 6: the button said "famine" with 3 in store and 4 on
    // the way, and then nothing happened - "nobody died" - so the warning
    // was wrong, not the night.
    undoState() { return this.dayRole ? undoCheck(this.game, this.dayRole) : { ok: false, why: '', label: '' }; },
    upkeepPreview() {
      const { civ } = this.game;
      const eaten = foodEatenPerRound(civ);
      const coming = villagesOf(civ).reduce((n, v) => n + (villageYield(civ, v).food || 0), 0);
      const have = civ.food + coming;
      const from = coming ? `${civ.food} in store + ${coming} from the villages` : `${civ.food} in store`;
      if (have < eaten) return `${eaten} food to eat, ${from}: FAMINE`;
      return `${eaten} food eaten (${from}), ${have - eaten} left`;
    },
  },
  watch: {
    techKey: { handler() { this.noticeTechs(); }, immediate: true },
    buildKey: { handler() { this.noticeBuildings(); }, immediate: true },
    counselorKey: { handler() { this.noticeCounselors(); }, immediate: true },
    // A split vote that lands while this phone is looking gets its moment.
    voteResult(now, was) {
      if (!now || was || !now.split) return;
      const labels = Object.fromEntries(this.choices.map((c) => [c.id, c.label]));
      this.spin = { tally: now.tally, picked: now.picked, labels };
    },
    // The act's own entry, once the reducer (or the room) has it.
    logCount() {
      const a = this.awaiting;
      if (!a) return;
      if (Date.now() - a.at > 15000) { this.awaiting = null; return; }
      const base = this.game.logBase || 0;
      const entry = this.game.log.slice(Math.max(0, a.from - base)).filter((l) => l.kind === 'act' && l.role === a.role).at(-1);
      if (!entry) return;
      this.awaiting = null;
      this.showOutcome(entry);
    },
    // The first time this phone holds a role and the day begins, open its guide.
    dayRole: {
      handler(role) {
        help.role = role || null;
        if (!role || ![PHASES.DAY, PHASES.DAWN].includes(this.phase)) return;
        this.maybeGuide(role);
      },
      immediate: true,
    },
    phase(now) {
      if ([PHASES.DAY, PHASES.DAWN].includes(now) && this.dayRole) this.maybeGuide(this.dayRole);
    },
  },
  beforeUnmount() {
    help.role = null;
  },
  methods: {
    // "1 worker left" / "3 shifts left" on a lone role tab (the learner:
    // "Hall 1" and "1 LEFT" meant nothing).
    leftWords(role) {
      if (role === 'culture') return hallLeftWords(civFor(this.game, 'culture'));
      const n = this.hands[role] || 0;
      if (role === 'trade') return `${n} ${n === 1 ? 'shift' : 'shifts'} left`;
      if (role === 'military') return `${n} ${n === 1 ? 'action' : 'actions'} left`;
      return `${n} ${n === 1 ? 'worker' : 'workers'} left`;
    },
    // Tokens back to people: the Stewards place half-days, but a tab that
    // counts heads must count heads.
    workersFor,
    hallLeft,
    civFor,
    // The first time this phone holds a role in a day, offer its tutorial
    // (once: "Not now" or finishing it both count).
    maybeGuide(role) {
      if (this.tutorial) return;
      // A dev preview is for looking at the board, not the offer over it -
      // unless it asks for it (&offer=1: the learner runs, tools/learner).
      const q = import.meta.env.DEV ? new URLSearchParams(window.location.search) : null;
      if (q?.has('preview') && !q.has('offer')) return;
      try {
        if (!localStorage.getItem(GUIDE_SEEN_KEY(role)) && !localStorage.getItem(`commonwealth:tutorial-done:${role}`)) this.offerFor = role;
      } catch { /* storage off */ }
    },
    closeOffer() {
      try { if (this.offerFor) localStorage.setItem(GUIDE_SEEN_KEY(this.offerFor), '1'); } catch { /* storage off */ }
      this.offerFor = null;
    },
    takeTutorial() {
      const role = this.offerFor;
      this.closeOffer();
      this.$store.dispatch('startTutorial', role);
    },
    allocate({ role, delta }) { this.$store.dispatch('allocate', { role, delta }); },
    propose(split) {
      // "Same as yesterday": nudge each role to yesterday's count.
      const now = this.game.allocation || {};
      ROLES.forEach((r) => {
        const delta = (split[r] || 0) - (now[r] || 0);
        if (delta) this.$store.dispatch('allocate', { role: r, delta });
      });
    },
    startDay(opts) { this.$store.dispatch('startDay', opts || {}); },
    // Done moves the phone on to the next role you hold that is still at
    // work (Matt, 2026-09-25: "the done button should take me to my next
    // role if I have multiple roles").
    done(role) {
      this.$store.dispatch('done', role);
      const i = this.myRoles.indexOf(role);
      const order = [...this.myRoles.slice(i + 1), ...this.myRoles.slice(0, i)];
      const next = order.find((r) => r !== role && !this.game.done?.[r]);
      if (next) this.chosenPreview = next;
      this.$nextTick(() => this.$el?.querySelector?.('.phone__body')?.scrollTo?.({ top: 0 }));
    },
    undone(role) { this.$store.dispatch('undone', role); },
    undo(role) { this.$store.dispatch('undo', role); },
    // Every board's act lands here. With the confirm on, it waits.
    act(payload) {
      const key = `${payload.role}:${payload.actionId}`;
      // Playtest 7: the dialog "wouldn't release" after Ponder/Study - the
      // act had landed, but Do it and Never mind did nothing until a
      // refresh. Most likely the tap that closed it fell through to the
      // button underneath and opened it again. So a tap straight after a
      // dialog closes is ignored, and a dialog never opens for an act the
      // rules would refuse.
      if (Date.now() - (this.confirmClosedAt || 0) < 400) return;
      // The tutorial is never asked about: its coach says what each step
      // does, and a dialog it did not expect only got in the way (playtest
      // oUrHs, Matt: "that's not helpful because the tutorial didn't expect it").
      if (this.tutorial) { this.fire(payload); return; }
      if (!confirmWanted(key)) { this.fire(payload); return; }
      // A step on the map is never asked about: it is the commonest act in
      // the game and Undo takes it back (playtest 11, Matt: "I don't need a
      // confirmation dialog every time I move a guy").
      if (payload.role === 'military' && payload.actionId === 'move') { this.fire(payload); return; }
      if (!checkAction(this.game, payload.role, payload.actionId, payload.params || {}).ok) { this.fire(payload); return; }
      const preview = this.previewAct(payload);
      if (!preview) { this.fire(payload); return; }
      this.confirmSkip = false;
      this.confirm = { ...preview, key, go: () => this.fire(payload) };
    },
    /** Dispatch an act and watch for its log entry, which becomes the outcome card. */
    fire(payload) {
      // No outcome card in the tutorial either (same playtest): the coach is the story there.
      this.awaiting = settings.outcome && !this.tutorial ? { role: payload.role, from: logTotal(this.game), at: Date.now() } : null;
      this.$store.dispatch('act', payload);
    },
    /** The act's entry has landed: draw it. */
    showOutcome(entry) {
      // A mix has its own reveal on the Scholars' board (MixReveal).
      if (entry.role === 'science' && entry.action === 'mix') return;
      const order = [...SPENDABLE, ...COUNTED];
      const pips = (patch) => order.filter((k, i) => order.indexOf(k) === i && patch?.[k]).map((k) => ({ kind: k, n: patch[k] }));
      const rolls = entry.rolls || null;
      this.outcome = {
        kind: ROLE_INFO[entry.role]?.label || entry.role,
        title: entry.label || entry.action,
        paid: pips(entry.paid),
        gained: pips(entry.gained),
        said: String(entry.said || '').replace(/^paid [^;]+; /, '').replace(/^\w/, (c) => c.toUpperCase()),
        rolls,
        sides: entry.sides,
        phase: null,
        each: (rolls || []).map(() => 900 + Math.round(Math.random() * 700)),
        colour: ROLE_INFO[entry.role]?.colour || null,
      };
      // Die3d tumbles on a CHANGE of phase: mount at rest, then throw.
      if (rolls) setTimeout(() => { if (this.outcome && this.outcome.rolls === rolls) this.outcome.phase = 'tumble'; }, 40);
    },
    closeOutcome() {
      this.outcome = null;
      this.confirmClosedAt = Date.now();
    },
    // Technologies this phone has been told about, per room, so a reload
    // (phones reload at dawn) neither repeats a notice nor loses one.
    techSeenKey() { return `commonwealth:tech-seen:${this.roomId || 'local'}`; },
    noticeTechs() {
      if (!this.game?.civ || this.tutorial) return;
      const techs = this.game.civ.techs || [];
      let seen = null;
      try { seen = JSON.parse(localStorage.getItem(this.techSeenKey()) || 'null'); } catch { seen = null; }
      // First sight of this game: nothing already known is news.
      if (!Array.isArray(seen)) { seen = [...techs]; try { localStorage.setItem(this.techSeenKey(), JSON.stringify(seen)); } catch { /* storage off */ } return; }
      const fresh = techs.filter((id) => !seen.includes(id));
      if (!fresh.length) return;
      try { localStorage.setItem(this.techSeenKey(), JSON.stringify([...seen, ...fresh])); } catch { /* storage off */ }
      // The Scholars had the eureka; everyone else hears what it does for THEM.
      const mine = this.myRoles.filter((r) => r !== 'science');
      if (!mine.length) return;
      fresh.forEach((id) => {
        const t = TECHS[id];
        if (!t) return;
        const who = t.forRole === 'any' ? mine : (Array.isArray(t.forRole) ? t.forRole : [t.forRole]).filter((r) => mine.includes(r));
        if (!who.length) return;
        // A text split by role ("Frontier: ... Stewards: ...", playtest 13)
        // shows this phone only its own clause(s).
        const clauses = t.text.split(/(?=\b(?:Frontier|Stewards|Scholars|Hall):)/).map((x) => x.trim()).filter(Boolean);
        const mineOnly = clauses.length > 1 ? clauses.filter((x) => who.some((r) => x.startsWith(`${ROLE_INFO[r].label}:`))) : [];
        const text = mineOnly.length && mineOnly.length < clauses.length ? mineOnly.map((x) => x.replace(/^\w+:\s*/, '')).join(' ') : t.text;
        // What it opens for this role, by name (playtest 13: Writing's card
        // told the Hall about "a card I don't have").
        const opens = [
          ...Object.values(HALL_CARDS).filter((c) => c.needsTech === id && who.includes('culture')).map((c) => c.name),
          ...Object.values(BUILDINGS).filter((b) => b.needsTech === id && who.includes('trade')).map((b) => `the ${b.name}`),
          ...Object.entries(COUNSELORS).filter(([, c]) => c.ask && /learn \w+/.test(c.ask) && c.ask.includes(t.name) && who.includes('culture')).map(([, c]) => `${c.name} on the council`),
        ];
        this.techQueue.push({ id, kind: 'tech', name: t.name, text, unlocks: opens.join(', '), roles: who.map((r) => ROLE_INFO[r].label).join(' and '), forAll: t.forRole === 'any', colour: ROLE_INFO[who[0]]?.colour || null });
      });
      this.nextTechNews();
    },
    // Someone's moment has come: the Hall's phone gets a card saying who
    // could sit and what it takes (playtest 14: "I was told the Scholar
    // could sit - what happened?"). Seen ids are kept per room.
    noticeCounselors() {
      if (!this.game?.civ?.hall || this.tutorial || !this.myRoles.includes('culture')) return;
      const key = `commonwealth:counselor-seen:${this.roomId || 'local'}`;
      const ready = emerging(this.game.civ, this.game.meta?.round || 1);
      let seen = null;
      try { seen = JSON.parse(localStorage.getItem(key) || 'null'); } catch { seen = null; }
      if (!Array.isArray(seen)) { try { localStorage.setItem(key, JSON.stringify(ready)); } catch { /* storage off */ } return; }
      const fresh = ready.filter((id) => !seen.includes(id));
      if (!fresh.length) return;
      try { localStorage.setItem(key, JSON.stringify([...new Set([...seen, ...fresh])])); } catch { /* storage off */ }
      const full = councilOf(this.game.civ).length >= 3;
      fresh.forEach((id) => {
        const c = COUNSELORS[id];
        if (!c) return;
        this.techQueue.push({ id: `counselor:${id}`, kind: 'counselor', name: `${c.name}, ${c.job}`, text: `${c.likes?.text ? `${c.likes.text.replace(/^likes/, 'Likes')}. ` : ''}${full ? 'The council is full: they are put forward when a chair empties, or the Hall can send someone home.' : 'A chair is empty: they may be put forward for it.'}`, roles: 'Hall', forAll: false, colour: ROLE_INFO.culture.colour });
      });
      this.nextTechNews();
    },
    // A building that helps another role pops a card on that phone like a
    // tech does (playtest 13: Matt built Andrew a library and had to explain it).
    noticeBuildings() {
      if (!this.game?.civ?.city || this.tutorial) return;
      const key = `commonwealth:build-seen:${this.roomId || 'local'}`;
      const ids = (this.game.civ.city.buildings || []).map((b) => b.id);
      let seen = null;
      try { seen = JSON.parse(localStorage.getItem(key) || 'null'); } catch { seen = null; }
      if (!Array.isArray(seen)) { try { localStorage.setItem(key, JSON.stringify(ids)); } catch { /* storage off */ } return; }
      const fresh = (this.game.civ.city.buildings || []).filter((b) => !seen.includes(b.id));
      if (!fresh.length) return;
      try { localStorage.setItem(key, JSON.stringify(ids)); } catch { /* storage off */ }
      const mine = this.myRoles.filter((r) => r !== 'trade');
      fresh.forEach((b) => {
        const def = BUILDINGS[b.type];
        if (!def?.helps || !mine.includes(def.helps)) return;
        this.techQueue.push({ id: b.id, kind: 'building', name: def.name, text: def.text.replace(/ Needs [^.]+ - ask the Scholars\./, ''), roles: ROLE_INFO[def.helps].label, forAll: false, colour: ROLE_INFO[def.helps]?.colour || null });
      });
      this.nextTechNews();
    },
    nextTechNews() { if (!this.techNews && this.techQueue.length) this.techNews = this.techQueue.shift(); },
    closeTechNews() { this.techNews = null; this.$nextTick(this.nextTechNews); },
    // The world's decision is everyone's; it asks too, with the card's own
    // words for what the choice does.
    resolve(choice) {
      const key = 'event:choice';
      const c = this.choices.find((x) => x.id === choice);
      if (!confirmWanted(key) || !c) { this.$store.dispatch('vote', { choice }); return; }
      const card = EVENTS[this.game.event?.cardId];
      const effect = [c.effectText, c.consequence].filter(Boolean).join('. ');
      this.confirmSkip = false;
      this.confirm = {
        key, kind: card?.name || 'The world turns', title: c.label, role: '',
        cost: {}, gainPips: [], gainText: c.text || (effect ? effect : 'Nothing changes now.') + (c.story ? ' More cards follow.' : ''),
        blurb: '', short: 'decisions on cards', colour: null,
        go: () => this.$store.dispatch('vote', { choice }),
      };
    },
    confirmGo() {
      const c = this.confirm;
      if (!c) return;
      if (this.confirmSkip) skipConfirm(c.key);
      this.closeConfirm();
      c.go();
    },
    closeConfirm() {
      this.confirm = null;
      this.confirmClosedAt = Date.now();
    },
    // Run the act on a copy and read what moved. Anything a die or the
    // glass would reveal is left out: the action's own words stand in.
    previewAct({ role, actionId, params = {} }) {
      const action = actionsForRole(role).find((a) => a.id === actionId);
      if (!action) return null;
      const civ = civFor(this.game, role);
      const after = runReducer(this.game, actAction(role, actionId, params));
      if (after === this.game) return null; // refused: let the board's own reason show
      const entry = after.log[after.log.length - 1];
      if (!entry || entry.kind !== 'act' || entry.role !== role) return null;
      const seenGrew = (after.civ.seen || []).length > (this.game.civ.seen || []).length;
      const reveals = Boolean(entry.rolls) || actionId === 'mix' || actionId === 'fire' || actionId === 'hint' || seenGrew || Boolean(entry.regionId);
      const order = [...SPENDABLE, ...COUNTED];
      const gained = reveals ? {} : (entry.gained || {});
      const gainPips = order.filter((k, i) => order.indexOf(k) === i && gained[k]).map((k) => ({ kind: k, n: gained[k] }));
      const said = reveals ? '' : String(entry.said || '').replace(/^paid [^;]+; /, '');
      return {
        role,
        kind: ROLE_INFO[role]?.label || role,
        // The card by name, not "Play a card" (playtest 14: the box "does not
        // say what it is confirming").
        title: `${entry.label || resolveLabel(action, civ)}${params.card && HALL_CARDS[params.card] ? `: ${HALL_CARDS[params.card].name}` : ''}`,
        cost: entry.paid && Object.keys(entry.paid).length ? entry.paid : resolveCost(action, civ, params.times || 1, params, this.game),
        gainPips,
        gainText: reveals ? (typeof action.gain === 'function' ? action.gain(civ) : action.gain) : (gainPips.length ? '' : said || 'nothing you can count'),
        blurb: reveals ? `${action.blurb || ''} ${entry.rolls || actionId === 'mix' || actionId === 'fire' || actionId === 'hint' ? 'No undo after this.' : 'This may reveal new ground; no undo after that.'}`.trim() : (action.blurb || ''),
        short: (entry.label || resolveLabel(action, civ)).toLowerCase(),
        colour: ROLE_INFO[role]?.colour || null,
      };
    },
    continueRound() { this.$store.dispatch('continueRound'); },
    voteFlip(force) { this.$store.dispatch('voteFlip', force); },
    decideNow() { this.$store.dispatch('vote', { choice: this.myVote, force: true }); },
    firstName(id) { return (this.playerName(id) || 'someone').split(' ')[0]; },
    votersFor(choice) {
      const votes = this.game.event?.votes || {};
      return Object.keys(votes).filter((id) => votes[id] === choice).map((id) => this.firstName(id));
    },
    leave() {
      if (this.roomId) this.$store.dispatch('leaveRoom');
      else this.$store.dispatch('abandonGame');
    },
  },
};
</script>
